import { z } from 'zod';

import { GuardianInviteSchema } from '../guardian-invite/guardian-invite';

/**
 * The one response that carries a code — the answer to the request that minted
 * it, and nowhere else, ever.
 *
 * A **separate schema** rather than a `code?: string` on
 * {@link GuardianInviteSchema}, because an optional secret is a secret somebody
 * will one day return by accident: a list endpoint that spread the row, a mapper
 * that forgot to omit a field, a `.partial()` taken for reuse. Here the type
 * itself says the code exists in exactly one place, and a route that wanted to
 * leak it would have to name this schema to do it.
 *
 * It is `.strict()` like every other contract, but the point of the split is not
 * validation — it is that `GuardianInvite` **cannot express a code at all**.
 */
export const GuardianInviteCreatedSchema = z
  .object({
    invite: GuardianInviteSchema,
    /**
     * The code, in its grouped display form (`4K7M-9P2R-XTQ3`).
     *
     * Formatted rather than raw because this value's entire job is to be read
     * off a screen and typed — or said out loud — by somebody else, and the
     * client must not have to know how the platform groups its own codes. The
     * accept route normalizes whatever comes back, so the grouping is free.
     */
    code: z.string().min(1),
  })
  .strict();

export type GuardianInviteCreated = z.infer<typeof GuardianInviteCreatedSchema>;
